import { useState } from "react"
import { useField } from "formik"
import { enqueueSnackbar } from "notistack"
import { Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"

import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"

import { DEFAULT_MINIMUM_DURATION_MINUTES } from "../../../createOrder/components/resource/resourceBookingCalendars/helpers"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { BookingCustomFieldsGroup } from "../../../createOrder/components/form/formTypes"

const useStyles = makeStyles()((_theme) => ({
    pickerList: {
        width: "4.375rem",
    },
}))

type TimeRangePickerProps = {
    timeRange: TimelineCalendarEventTimeRange
}

export function TimeRangePicker({ timeRange }: TimeRangePickerProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const [{ value: startDateTime }, , startHelpers] =
        useField<BookingCustomFieldsGroup["startDateTime"]>("startDateTime")
    const [{ value: endDateTime }, , endHelpers] = useField<BookingCustomFieldsGroup["endDateTime"]>("endDateTime")

    const minTime = dateTimeFactory.fromISO(timeRange.start)
    const rawMaxTime = dateTimeFactory.fromISO(timeRange.end)

    // MUI TimePicker treats maxTime=00:00 as "start of day", which may block all options.
    // If the time range ends exactly at midnight, we interpret it as "end of previous day".
    const maxTime =
        rawMaxTime <= minTime
            ? rawMaxTime.plus({ days: 1 }).minus({ millisecond: 1 })
            : rawMaxTime.hour === 0 &&
                rawMaxTime.minute === 0 &&
                rawMaxTime.second === 0 &&
                rawMaxTime.millisecond === 0
              ? rawMaxTime.minus({ millisecond: 1 })
              : rawMaxTime

    const maxTimeForFromPicker =
        maxTime <= minTime ? minTime : maxTime.minus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })

    const startTime = dateTimeFactory.fromISO(startDateTime)
    const endTime = dateTimeFactory.fromISO(endDateTime)

    const handleShowErrorSnackbar = () => {
        enqueueSnackbar({
            variant: "warning",
            message: l10n.getString("create-order-confirm-booking-time-range-picker-error"),
        })
    }

    const [openFrom, setOpenFrom] = useState(false)
    const [openTo, setOpenTo] = useState(false)

    return (
        <Stack flexDirection="row" alignItems="center" gap="1rem" width="60%">
            <LocalizedStrict id="create-order-confirm-booking-time-range-picker-from-field" attrs={{ label: true }}>
                <TimePicker
                    value={startTime}
                    onChange={(newTime) => {
                        if (newTime && newTime >= minTime && newTime < maxTime) {
                            let adjustedEndTime = endTime

                            if (!endTime || newTime >= endTime) {
                                adjustedEndTime = newTime.plus({ minutes: 15 })

                                if (adjustedEndTime > maxTime) {
                                    adjustedEndTime = maxTime
                                }

                                endHelpers.setValue(adjustedEndTime.toISO())
                            }

                            startHelpers.setValue(newTime.toISO())
                        } else {
                            handleShowErrorSnackbar()
                            newTime && startHelpers.setValue(newTime.toISO())
                        }
                    }}
                    label="From time:"
                    minTime={minTime}
                    maxTime={maxTimeForFromPicker}
                    ampm={false}
                    open={openFrom}
                    onOpen={() => setOpenFrom(true)}
                    onClose={() => setOpenFrom(false)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: "outlined",
                            onClick: () => setOpenFrom(true),
                        },
                        popper: {
                            sx: {
                                "& .MuiList-root": classes.pickerList,
                            },
                        },
                    }}
                />
            </LocalizedStrict>

            <LocalizedStrict id="create-order-confirm-booking-time-range-picker-to-field" attrs={{ label: true }}>
                <TimePicker
                    value={endTime}
                    onChange={(newTime) => {
                        if (
                            newTime &&
                            newTime > startTime.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES }) &&
                            newTime <= maxTime
                        ) {
                            endHelpers.setValue(newTime.toISO())
                        } else {
                            handleShowErrorSnackbar()
                            newTime && endHelpers.setValue(newTime.toISO())
                        }
                    }}
                    label="To time:"
                    minTime={startTime.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })}
                    maxTime={maxTime}
                    ampm={false}
                    open={openTo}
                    onOpen={() => setOpenTo(true)}
                    onClose={() => setOpenTo(false)}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: "outlined",
                            onClick: () => setOpenTo(true),
                        },
                        popper: {
                            sx: {
                                "& .MuiList-root": classes.pickerList,
                            },
                        },
                    }}
                />
            </LocalizedStrict>
        </Stack>
    )
}
